import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { useTranslation } from 'react-i18next';
import { AREA_COLORS } from 'constants/surveys';
import { useWindowWidth } from 'hooks/useWindowWidth';
import { MOBILE_MAX_WIDTH_PX } from '../../types';
import { ProgressRing } from './ProgressRing';
import { AREA_LUCIDE_ICONS, type WellBeingAreaProps } from './types';

export const WellBeingArea = ({
  area,
  score = 0,
  maxScore = 0,
  showScore = false,
  className,
}: WellBeingAreaProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.WELL_BEING);
  const { width } = useWindowWidth();
  const isDesktop = width >= MOBILE_MAX_WIDTH_PX;
  const ringColor = AREA_COLORS[area];
  const AreaIcon = AREA_LUCIDE_ICONS[area];
  const normalizedProgress = maxScore > 0 ? Math.min(Math.max(score / maxScore, 0), 1) : 0;
  const scoreLabel = `${score}/${maxScore}`;

  return (
    <div
      className={cn(
        'flex items-center rounded-xl bg-white px-4 py-2',
        showScore
          ? 'h-10 w-full gap-2 shadow-box-sm am-xl:h-16 am-xl:w-66 am-xl:gap-4 am-xl:py-2'
          : 'h-13 w-66 gap-4 shadow-box-xs',
        className
      )}
    >
      <AreaIcon
        className={cn('shrink-0 text-plum-300', showScore ? 'size-6 am-xl:size-8' : 'size-8')}
        style={showScore ? { color: ringColor } : undefined}
        strokeWidth={1.5}
        aria-hidden
      />
      <p className="min-w-0 flex-1 text-denim-500 text-xs leading-5 am-xl:text-sm">
        {t(`BarometerResults.Areas.${area}`)}
      </p>
      {showScore && (
        <div className={cn('flex shrink-0 items-center gap-2', isDesktop && 'relative')}>
          <span
            className={cn(
              'text-denim-500 text-xs leading-5',
              isDesktop
                ? 'absolute inset-1 flex items-center justify-center text-center font-semibold'
                : 'font-medium'
            )}
          >
            {scoreLabel}
          </span>
          <ProgressRing
            variant={isDesktop ? 'desktop' : 'mobile'}
            progress={normalizedProgress}
            color={ringColor}
          />
        </div>
      )}
    </div>
  );
};
